import type { Batch, BatchStatus, Currency, Location, Money, Product, ProductStatus } from './types'
import { daysUntil, NOW, BASE_CURRENCY, convert, money } from './format'

/* ─────────────────────────────  Batch-level  ───────────────────────────── */

/** Derived batch status (explicit holds win, then expiry, then depletion). */
export function batchStatus(b: Batch): BatchStatus {
  if (b.hold === 'quarantined') return 'quarantined'
  if (b.hold === 'damaged') return 'damaged'
  if (b.quantity <= 0) return 'depleted'
  const d = daysUntil(b.expiryDate)
  if (d < 0) return 'expired'
  if (d <= 30) return 'expiring'
  return 'active'
}

/** A batch counts toward available stock only if it's active or expiring. */
export function isUsable(b: Batch): boolean {
  const s = batchStatus(b)
  return s === 'active' || s === 'expiring'
}

/* ─────────────────────────────  Quantity rollups  ──────────────────────── */

/** Physical on-hand across all batches (incl. blocked/quarantined). */
export function onHand(batches: Batch[], productId: string, locationId?: string): number {
  return batches
    .filter((b) => b.productId === productId && (!locationId || b.locationId === locationId))
    .reduce((sum, b) => sum + b.quantity, 0)
}

/**
 * Available = usable on-hand minus what is already reserved for pending orders/transfers.
 * Expired and held batches are physically present but cannot be shipped, so they are excluded;
 * reserved units are shippable stock that is already spoken for. Never negative.
 */
export function available(batches: Batch[], productId: string, locationId?: string): number {
  const usable = batches.filter(
    (b) => b.productId === productId && (!locationId || b.locationId === locationId) && isUsable(b),
  )
  const onHandUsable = usable.reduce((sum, b) => sum + b.quantity, 0)
  const reserved = usable.reduce((sum, b) => sum + (b.reserved ?? 0), 0)
  return Math.max(0, onHandUsable - reserved)
}

/**
 * Where a product's stock actually sits. `onHand` is the physical count and equals
 * active + expiring + expired + quarantined. `damaged` is a segregated bucket that has ALREADY
 * been deducted from on-hand, so it never appears in that sum. `available` is what can ship.
 */
export interface StockBreakdown {
  onHand: number
  active: number
  expiring: number
  expired: number
  quarantined: number
  reserved: number
  damaged: number
  available: number
}

/** Splits a product's stock into the buckets shown on the product detail page. */
export function stockBreakdown(batches: Batch[], productId: string, locationId?: string): StockBreakdown {
  const rows = batches.filter(
    (b) => b.productId === productId && (!locationId || b.locationId === locationId),
  )

  const bucket = (status: BatchStatus) =>
    rows.filter((b) => batchStatus(b) === status).reduce((sum, b) => sum + b.quantity, 0)

  return {
    onHand: rows.reduce((sum, b) => sum + b.quantity, 0),
    active: bucket('active'),
    expiring: bucket('expiring'),
    expired: bucket('expired'),
    quarantined: bucket('quarantined'),
    reserved: rows.reduce((sum, b) => sum + (b.reserved ?? 0), 0),
    damaged: rows.reduce((sum, b) => sum + (b.damaged ?? 0), 0),
    available: available(batches, productId, locationId),
  }
}

/* ─────────────────────────────  Product status  ───────────────────────── */

export function daysOfStock(product: Product, batches: Batch[]): number | null {
  if (!product.avgDailyUsage) return null
  return Math.round(available(batches, product.id) / product.avgDailyUsage)
}

export function productStatus(product: Product, batches: Batch[]): ProductStatus {
  const avail = available(batches, product.id)
  if (avail <= 0) return 'out'
  if (avail <= product.reorderPoint) return 'low'
  const hasNearExpiry = batches.some(
    (b) => b.productId === product.id && isUsable(b) && daysUntil(b.expiryDate) <= 30,
  )
  if (hasNearExpiry) return 'expiring'
  return 'in_stock'
}

/** Map our domain statuses onto existing statusMeta keys for Pill/StatusBadge. */
export const productStatusKey: Record<ProductStatus, string> = {
  in_stock: 'inv_in_stock',
  low: 'inv_low',
  out: 'inv_out',
  expiring: 'inv_expiring',
}

/* ─────────────────────────────  Valuation  ─────────────────────────────
   Every value is a { amount, currency } Money pair. A location's currency
   comes from the location; a product's cost is in its own native currency.
   Single-location values use the location currency; cross-location totals
   convert to a single reporting currency. Nothing assumes a fixed set. */

const currencyOf = (locations: Location[], locationId: string): Currency =>
  locations.find((l) => l.id === locationId)?.currency ?? BASE_CURRENCY

/** Value of one product's on-hand at one location, in that location's currency. */
export function valueAt(
  batches: Batch[],
  product: Product,
  locations: Location[],
  locationId: string,
): Money {
  const cur = currencyOf(locations, locationId)
  const unit = convert(product.cost, cur).amount
  return money(onHand(batches, product.id, locationId) * unit, cur)
}

/** Total stock value across all locations, converted to one reporting currency. */
export function stockValue(
  batches: Batch[],
  products: Product[],
  locations: Location[],
  target: Currency = BASE_CURRENCY,
): Money {
  let total = 0
  for (const b of batches) {
    const product = products.find((p) => p.id === b.productId)
    if (!product) continue
    const cur = currencyOf(locations, b.locationId)
    const native = money(b.quantity * convert(product.cost, cur).amount, cur)
    total += convert(native, target).amount
  }
  return money(total, target)
}

/** One product's total value across locations, converted to one reporting currency. */
export function productValue(
  batches: Batch[],
  product: Product,
  locations: Location[],
  target: Currency = BASE_CURRENCY,
): Money {
  let total = 0
  for (const b of batches) {
    if (b.productId !== product.id) continue
    const cur = currencyOf(locations, b.locationId)
    const native = money(b.quantity * convert(product.cost, cur).amount, cur)
    total += convert(native, target).amount
  }
  return money(total, target)
}

/** Per-location breakdown for a product — each entry in its own native currency. */
export function productValueByLocation(
  batches: Batch[],
  product: Product,
  locations: Location[],
): { location: Location; value: Money }[] {
  return locations
    .filter((loc) => onHand(batches, product.id, loc.id) > 0)
    .map((loc) => ({ location: loc, value: valueAt(batches, product, locations, loc.id) }))
}

/** Stock value grouped by currency — each total kept in its own currency (for Reports). */
export function valueByCurrency(
  batches: Batch[],
  products: Product[],
  locations: Location[],
): Money[] {
  const totals = new Map<Currency, number>()
  for (const b of batches) {
    const product = products.find((p) => p.id === b.productId)
    if (!product) continue
    const cur = currencyOf(locations, b.locationId)
    totals.set(cur, (totals.get(cur) ?? 0) + b.quantity * convert(product.cost, cur).amount)
  }
  return [...totals.entries()].map(([currency, amount]) => money(amount, currency))
}

/* ─────────────────────────────  Expiry / FEFO  ─────────────────────────── */

/** Usable batches expiring within `days` (FEFO-sorted earliest first). */
export function expiringWithin(batches: Batch[], days: number): Batch[] {
  return batches
    .filter((b) => {
      if (!isUsable(b)) return false
      const d = daysUntil(b.expiryDate)
      return d >= 0 && d <= days
    })
    .sort((a, b) => new Date(a.expiryDate).getTime() - new Date(b.expiryDate).getTime())
}

export function expiredBatches(batches: Batch[]): Batch[] {
  return batches.filter((b) => batchStatus(b) === 'expired')
}

/** Units expiring by month for the next ~6 months (Recharts timeline). */
export function expiryTimeline(batches: Batch[]): { month: string; units: number; band: '30' | '60' | '90' | 'later' }[] {
  const months: { key: string; label: string; units: number; minDays: number }[] = []
  for (let i = 0; i < 6; i++) {
    const d = new Date(NOW.getFullYear(), NOW.getMonth() + i, 1)
    months.push({
      key: `${d.getFullYear()}-${d.getMonth()}`,
      label: d.toLocaleDateString('en-GB', { month: 'short' }),
      units: 0,
      minDays: i * 30,
    })
  }
  for (const b of batches) {
    if (!isUsable(b)) continue
    const exp = new Date(b.expiryDate)
    const key = `${exp.getFullYear()}-${exp.getMonth()}`
    const bucket = months.find((m) => m.key === key)
    if (bucket) bucket.units += b.quantity
  }
  return months.map((m) => ({
    month: m.label,
    units: m.units,
    band: m.minDays < 30 ? '30' : m.minDays < 60 ? '60' : m.minDays < 90 ? '90' : 'later',
  }))
}

/* ─────────────────────────────  Cross-cutting lists  ───────────────────── */

export function lowOrOutProducts(products: Product[], batches: Batch[]): Product[] {
  return products.filter((p) => {
    const s = productStatus(p, batches)
    return s === 'low' || s === 'out'
  })
}

export function batchesForProduct(batches: Batch[], productId: string): Batch[] {
  return batches
    .filter((b) => b.productId === productId)
    .sort((a, b) => new Date(a.expiryDate).getTime() - new Date(b.expiryDate).getTime())
}
